/**
 * Customer Address Update Action
 * Triggered by: arc:action:customerAddressUpdate
 */

export const runCustomerAddressUpdate = async (data = {}) => {
    try {
        const { 
            addressId,
            customerId,
            firstName, 
            lastName, 
            company = '',
            address1, 
            address2 = '',
            city,
            province,
            zip,
            country,
            phone = '',
            isDefault = false,
            form,
            action = 'update' // 'update' or 'create'
        } = data;

        // Validation
        if (action === 'update' && !addressId) {
            throw new Error('Address ID is required for updates');
        }

        if (!firstName || !lastName || !address1 || !city || !province || !zip || !country) {
            throw new Error('First name, last name, address, city, province, zip, and country are required');
        }

        // Prepare form data
        const formData = new FormData();
        formData.append('form_type', action === 'create' ? 'customer_address' : 'customer_address');
        formData.append('utf8', '✓');
        
        if (action === 'update' && addressId) {
            formData.append('_method', 'put');
        }
        
        formData.append('address[first_name]', firstName);
        formData.append('address[last_name]', lastName);
        formData.append('address[company]', company);
        formData.append('address[address1]', address1);
        formData.append('address[address2]', address2);
        formData.append('address[city]', city);
        formData.append('address[province]', province);
        formData.append('address[zip]', zip);
        formData.append('address[country]', country);
        formData.append('address[phone]', phone);
        
        if (isDefault) {
            formData.append('address[default]', '1');
        }

        // Dispatch attempt event
        window.dispatchEvent(new CustomEvent('customer:addressUpdateAttempt', {
            detail: {
                action,
                addressId,
                customerId,
                address: {
                    firstName,
                    lastName,
                    company,
                    address1,
                    address2,
                    city,
                    province,
                    zip,
                    country,
                    phone,
                    isDefault
                }
            }
        }));

        // Determine the URL
        let url;
        if (action === 'create') {
            url = '/account/addresses';
        } else {
            url = `/account/addresses/${addressId}`;
        }

        // Submit address request
        const response = await fetch(url, {
            method: 'POST',
            body: formData,
            headers: {
                'Accept': 'application/json, text/html',
            }
        });

        if (response.ok) {
            // Check if response is JSON (error) or HTML (success redirect)
            const contentType = response.headers.get('content-type');
            
            if (contentType && contentType.includes('application/json')) {
                // JSON response usually means validation errors
                const errorData = await response.json();
                const errors = errorData.errors || {};
                const errorMessages = Object.values(errors).flat();
                throw new Error(errorMessages.join(', ') || `Address ${action} failed`);
            } else {
                // HTML response - check for errors in the response
                const responseText = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(responseText, 'text/html');
                
                // Look for error messages
                const errorElements = doc.querySelectorAll('.errors li, .error, [data-error]');
                if (errorElements.length > 0) {
                    const errors = Array.from(errorElements).map(el => el.textContent.trim());
                    throw new Error(errors.join(', '));
                }
                
                // Success
                window.dispatchEvent(new CustomEvent('customer:addressUpdateSuccess', {
                    detail: {
                        action,
                        addressId: addressId || 'new',
                        address: {
                            firstName,
                            lastName,
                            company,
                            address1,
                            address2,
                            city,
                            province,
                            zip,
                            country,
                            phone,
                            isDefault
                        },
                        timestamp: Date.now()
                    }
                }));

                return {
                    success: true,
                    action,
                    addressId: addressId || 'new',
                    message: `Address ${action === 'create' ? 'created' : 'updated'} successfully`
                };
            }
        } else {
            // Handle HTTP errors
            let errorMessage = `Address ${action} failed`;
            
            try {
                const errorText = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(errorText, 'text/html');
                const errorElements = doc.querySelectorAll('.errors li, .error, [data-error]');
                
                if (errorElements.length > 0) {
                    const errors = Array.from(errorElements).map(el => el.textContent.trim());
                    errorMessage = errors.join(', ');
                }
            } catch (e) {
                console.error('Error parsing response:', e);
            }

            throw new Error(errorMessage);
        }

    } catch (error) {
        console.error('Customer Address Update error:', error);
        
        window.dispatchEvent(new CustomEvent('customer:addressUpdateError', {
            detail: {
                error: error.message,
                action: data.action,
                addressId: data.addressId
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Initialize address form listeners
export const initializeCustomerAddressUpdate = (config = {}) => {
    const {
        formSelector = '[data-customer-address-form]',
        autoSetupForms = true,
        enableAddressValidation = true
    } = config;

    if (autoSetupForms) {
        setupAddressUpdateForms(formSelector, enableAddressValidation);
    }

    window.dispatchEvent(new CustomEvent('customerAddressUpdate:initialized', {
        detail: { config, timestamp: Date.now() }
    }));
};

// Setup address form event listeners
const setupAddressUpdateForms = (formSelector, enableAddressValidation) => {
    const forms = document.querySelectorAll(formSelector);
    
    forms.forEach(form => {
        form.removeEventListener('submit', handleAddressUpdateSubmit);
        form.addEventListener('submit', handleAddressUpdateSubmit);

        if (enableAddressValidation) {
            // Add address validation listeners
            const countryField = form.querySelector('[name="address[country]"]');
            const provinceField = form.querySelector('[name="address[province]"]');
            
            if (countryField) {
                countryField.removeEventListener('change', handleCountryChange);
                countryField.addEventListener('change', handleCountryChange);
            }
        }
    });
};

// Handle address form submissions
const handleAddressUpdateSubmit = async (event) => {
    event.preventDefault();
    
    const form = event.target;
    const formData = new FormData(form);
    
    const addressId = form.dataset.addressId;
    const customerId = form.dataset.customerId;
    const action = addressId ? 'update' : 'create';
    
    const firstName = formData.get('address[first_name]');
    const lastName = formData.get('address[last_name]');
    const company = formData.get('address[company]');
    const address1 = formData.get('address[address1]');
    const address2 = formData.get('address[address2]');
    const city = formData.get('address[city]');
    const province = formData.get('address[province]');
    const zip = formData.get('address[zip]');
    const country = formData.get('address[country]');
    const phone = formData.get('address[phone]');
    const isDefault = formData.get('address[default]') === '1';

    // Disable submit button
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn?.textContent;
    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = action === 'create' ? 'Creating...' : 'Updating...';
    }

    try {
        await window.dispatchEvent(new CustomEvent('arc:action:customerAddressUpdate', {
            detail: {
                addressId,
                customerId,
                firstName,
                lastName,
                company,
                address1,
                address2,
                city,
                province,
                zip,
                country,
                phone,
                isDefault,
                form,
                action
            }
        }));
    } finally {
        // Re-enable submit button
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
        }
    }
};

// Handle country change for province/state updates
const handleCountryChange = (event) => {
    const form = event.target.closest('form');
    const country = event.target.value;
    const provinceField = form.querySelector('[name="address[province]"]');
    
    // Dispatch country change event
    window.dispatchEvent(new CustomEvent('customer:addressCountryChange', {
        detail: {
            country,
            form,
            provinceField
        }
    }));
    
    // Update province field options based on country
    updateProvinceOptions(country, provinceField);
};

// Update province/state options based on country
const updateProvinceOptions = (country, provinceField) => {
    if (!provinceField) return;
    
    // This would typically fetch province data from Shopify or use a predefined list
    // For now, we'll dispatch an event that can be handled by the theme
    window.dispatchEvent(new CustomEvent('customer:updateProvinceOptions', {
        detail: {
            country,
            provinceField
        }
    }));
};

// Helper function to validate address fields
export const validateAddressFields = (addressData) => {
    const errors = [];
    
    if (!addressData.firstName) errors.push('First name is required');
    if (!addressData.lastName) errors.push('Last name is required');
    if (!addressData.address1) errors.push('Address is required');
    if (!addressData.city) errors.push('City is required');
    if (!addressData.province) errors.push('Province/State is required');
    if (!addressData.zip) errors.push('Postal/Zip code is required');
    if (!addressData.country) errors.push('Country is required');
    
    return {
        isValid: errors.length === 0,
        errors
    };
};

export default runCustomerAddressUpdate;
